<template>
  <div class="search-container relative" :class="isDesktop ? 'w-auto' : 'w-full'">
    <!-- Form Tag -->
    <div
      v-if="destinationSelected"
      class="hidden md:flex absolute -top-3 right-4 bg-[#e0f7ef] text-[#4ECF84] text-xs font-semibold px-3 py-1 rounded-[20px] border border-gray-200 z-[70] shadow-sm whitespace-nowrap pointer-events-none items-center gap-1"
    >
      <Check class="w-3.5 h-3.5" />
    </div>
    <div :class="isDesktop ? 'relative' : 'sticky top-0 z-[60] bg-white pb-2'" ref="inputRef">
      <input
        v-model="searchQuery"
        @focus="handleFocus"
        @input="handleInput"
        @keydown.enter.prevent="handleEnterKey"
        @blur="handleBlur"
        @click.stop="handleFocus"
        :placeholder="placeholder"
        :size="isDesktop ? Math.max(9, (searchQuery?.length || 0)) + 2 : undefined"
        :style="isDesktop ? { 'field-sizing': 'content', 'min-width': '320px', 'max-width': '100%' } : {}"
        class="rounded-full border focus:outline-none shadow-sm transition-all"
        :class="isDesktop ? 'pl-6 pr-12 w-auto text-base font-semibold py-3 border-[#2F3033] text-[#2F3033] placeholder:text-[#2F3033] placeholder:text-base focus:outline-none' : 'px-4 w-full text-lg font-semibold py-3 border-[#2F3033] placeholder:text-lg placeholder:text-[#2F3033] placeholder:font-semibold'"
      />
      <!-- Close/Clear Icon -->
       <button
        v-if="searchQuery"
        type="button"
        class="absolute transform -translate-y-1/2"
        :class="isDesktop ? 'right-4 top-1/2 text-[#2F3033]' : 'right-[26px] top-[calc(50%-1px)] text-[#2F3033]'"
        @click.stop="clearSearch"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- Dropdown -->
      <div 
        v-if="isOpen" 
        class="dropdown-content bg-white mt-2 border border-[#2F3033] rounded-[32px] shadow-2xl z-[80] overflow-hidden"
        :class="isDesktop ? 'fixed p-2' : 'absolute w-full left-0 p-4'"
        :style="dropdownStyle"
        @click.stop
      >
        <!-- State A: Default/Focus (Empty Input) -->
        <div v-if="!searchQuery" class="py-1">
          
          <div class="px-5 mb-1.5 mt-1">
            <span class="text-xs text-gray-400 font-normal uppercase tracking-wider">Suggested destination</span>
          </div>
  
          <div @click="selectNearby" class="px-5 py-2 cursor-pointer flex items-center gap-3 transition-colors group border-b border-gray-100 mb-2">
            <MapPin class="w-4 h-4 text-[#2F3033]" />
            <span class="text-[#2F3033] group-hover:font-semibold">Nearby</span>
          </div>
          
          <div class="mt-2">
            <div class="text-[#2F3033] px-5 py-1 text-sm">
              <span class="font-semibold">{{ currentCountry }}</span> <span class="text-gray-400 font-normal">(Local)</span>
            </div>
            <div class="space-y-1">
              <div 
                v-for="loc in localSuggestions" 
                :key="loc" 
                @click="searchQuery = loc; selectByText(loc)" 
                class="py-1.5 px-5 cursor-pointer text-[#2F3033] transition-colors hover:font-semibold hover:bg-gray-50"
              >
                {{ loc }}
              </div>
            </div>
          </div>
        </div>
  
        <!-- State B: Active Typing -->
        <div v-else class="py-2">
          <div v-if="isLoading && filteredResults.length === 0" class="p-4 text-center text-gray-500">
            <div class="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-[#4ECF84] rounded-full" role="status" aria-label="loading">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
          
          <div v-else-if="filteredResults.length === 0" class="p-4 text-center text-gray-500">
            No destinations found.
          </div>
          
          <div v-else class="space-y-1">
            <div 
              v-for="result in filteredResults" 
              :key="result.place_id" 
              @click="select(result)" 
              class="px-5 py-2 cursor-pointer flex flex-col transition-colors hover:font-semibold hover:bg-gray-50"
            >
              <div class="text-[#2F3033] truncate text-[15px]" v-html="highlight(result.structured_formatting.main_text, result.structured_formatting.secondary_text)"></div>
            </div>
          </div>
          <!-- Small Loader at bottom when fetching more -->
          <div v-if="isLoading" class="p-2 text-center text-gray-400">
             <svg class="animate-spin h-4 w-4 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>

      </div>
    </div>
    
    <!-- Backdrop to close dropdown -->
    <div v-if="isOpen" class="fixed inset-0 z-40 bg-transparent" @click="isOpen = false"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import DOMPurify from 'dompurify'

const props = defineProps({
  placeholder: { type: String, default: 'Where to?' }
})

import { loadGoogleMapsAPI } from '@/utils/googleMapsLoader'
import { Check, MapPin } from 'lucide-vue-next'

const emit = defineEmits(['select'])

const searchQuery = ref('')
const isOpen = ref(false)
const isLoading = ref(false)
const filteredResults = ref([])
const currentCountry = ref('Australia')
const sessionToken = ref(null)
const inputRef = ref(null)
const destinationSelected = ref(false)

// Responsive state for positioning
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
const isDesktop = computed(() => windowWidth.value >= 768)

const dropdownStyle = computed(() => {
  if (!isOpen.value || !inputRef.value) return {}
  
  const rect = inputRef.value.getBoundingClientRect()
  const width = rect.width
  
  if (isDesktop.value) {
    return {
      top: `${rect.bottom + 12}px`, 
      left: `${rect.left}px`,
      width: `${width}px`,
      position: 'fixed',
      zIndex: 9999 
    }
  } 
  
  // Mobile: default to empty (relative positioning via classes)
  return {}
})

// Static Data for "International" logic
const staticDestinations = [
  // Regions
  { name: "Africa", type: "Region", region: "Global" },
  { name: "Americas", type: "Region", region: "Global" },
  { name: "Asia", type: "Region", region: "Global" },
  { name: "Europe", type: "Region", region: "Global" },
  { name: "Oceania", type: "Region", region: "Global" },
  // Americas
  { name: "United States", type: "Country", region: "North America" },
  { name: "Canada", type: "Country", region: "North America" },
  { name: "Mexico", type: "Country", region: "North America" },
  { name: "Brazil", type: "Country", region: "South America" },
  { name: "Argentina", type: "Country", region: "South America" },
  { name: "Colombia", type: "Country", region: "South America" },
  { name: "Cartagena", type: "City", region: "Colombia" },
  // Europe
  { name: "France", type: "Country", region: "Europe" },
  { name: "Paris", type: "City", region: "France" },
  { name: "Italy", type: "Country", region: "Europe" },
  { name: "Spain", type: "Country", region: "Europe" },
  { name: "United Kingdom", type: "Country", region: "Europe" },
  { name: "Greece", type: "Country", region: "Europe" },
  { name: "Portugal", type: "Country", region: "Europe" },
  { name: "Germany", type: "Country", region: "Europe" },
  { name: "Netherlands", type: "Country", region: "Europe" },
  // Asia/Pacific
  { name: "Japan", type: "Country", region: "East Asia" },
  { name: "Tokyo", type: "City", region: "Japan" },
  { name: "Thailand", type: "Country", region: "Southeast Asia" },
  { name: "Indonesia", type: "Country", region: "Southeast Asia" },
  { name: "Bali", type: "City", region: "Indonesia" },
  { name: "Singapore", type: "Country", region: "Southeast Asia" },
  { name: "South Korea", type: "Country", region: "East Asia" },
  { name: "India", type: "Country", region: "South Asia" },
  { name: "New Zealand", type: "Country", region: "Oceania" },
  // Middle East/Africa
  { name: "United Arab Emirates", type: "Country", region: "Middle East" },
  { name: "Dubai", type: "City", region: "United Arab Emirates" },
  { name: "Turkey", type: "Country", region: "Middle East/Europe" },
  { name: "Egypt", type: "Country", region: "North Africa" },
  { name: "Morocco", type: "Country", region: "North Africa" },
  { name: "South Africa", type: "Country", region: "Africa" },
  { name: "Maldives", type: "Country", region: "South Asia" }
]

// Mock local suggestions
const localSuggestions = computed(() => {
  if (currentCountry.value === 'Australia') {
    return ['Melbourne, Victoria', 'Sydney, New South Wales', 'Gold Coast, Queensland', 'Byron Bay, New South Wales', 'Hobart, Tasmania']
  }
  return []
})

const debounce = (fn, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

const handleInput = debounce(() => {
  // Reset selection state so the parent knows this field is no longer confirmed
  if (destinationSelected.value) {
    destinationSelected.value = false
    emit('select', null)
  }
  if (!searchQuery.value) {
    filteredResults.value = []
    return
  }
  search()
}, 300)

// Accept typed text when user presses Enter
const handleEnterKey = () => {
  if (!searchQuery.value) return
  if (filteredResults.value.length > 0) {
    // Select the best match from the dropdown
    select(filteredResults.value[0])
  } else {
    // No suggestions yet — accept the raw typed text
    selectByText(searchQuery.value)
  }
  isOpen.value = false
}

// Accept typed text when user leaves the field without clicking a suggestion
const handleBlur = () => {
  // Delay so a dropdown item click can fire select() before we evaluate
  setTimeout(() => {
    if (searchQuery.value && !destinationSelected.value) {
      selectByText(searchQuery.value)
    }
    isOpen.value = false
  }, 200)
}

const clearSearch = () => {
  searchQuery.value = ''
  filteredResults.value = []
  destinationSelected.value = false
  emit('select', null)
}

// Watch searchQuery and emit if it changes but isn't explicitly selected
watch(searchQuery, (newVal) => {
  if (newVal && !destinationSelected.value) {
     emit('select', { description: newVal })
  } else if (!newVal) {
     emit('select', null)
  }
})

const scrollInputIntoView = () => {
  if (!isDesktop.value && inputRef.value && isOpen.value) {
    inputRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const handleFocus = () => {
  isOpen.value = true
  
  if (!isDesktop.value && inputRef.value) {
    // Wait for keyboard to fully appear before scrolling (keyboard ~300ms animation)
    setTimeout(() => {
      scrollInputIntoView()
    }, 400)
  }
}

// Updated Bolding/Highlighting logic (using span class="font-bold")
const highlight = (mainText, secondaryText) => {
  const safeMain = DOMPurify.sanitize(mainText || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const safeSecondary = secondaryText ? DOMPurify.sanitize(secondaryText, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) : '';
  const fullText = safeSecondary ? `${safeMain}, ${safeSecondary}` : safeMain;
  if (!searchQuery.value) return fullText;

  try {
    // Escape special regex chars
    const escapedQuery = searchQuery.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return fullText.replace(regex, '<span class="font-bold">$1</span>');
  } catch (e) {
    return fullText;
  }
}

// Enhanced Search Logic
const search = async () => {
  const query = searchQuery.value
  if (!query || query.length < 2) return // Wait for at least 2 chars? Prompt said "3+ characters switch to Global", assuming we show results starting 2+
  
  isLoading.value = true
  
  // 1. Static Filtering Strategy
  // Direct Matches (Starts with)
  const queryLower = query.toLowerCase()
  const staticMatches = staticDestinations.filter(item => 
    item.name.toLowerCase().includes(queryLower)
  ).sort((a, b) => {
    // Weighting: Direct start matches first
    const aStarts = a.name.toLowerCase().startsWith(queryLower)
    const bStarts = b.name.toLowerCase().startsWith(queryLower)
    if (aStarts && !bStarts) return -1
    if (!aStarts && bStarts) return 1
    return 0
  }).map(item => ({
    place_id: `static-${item.name}`,
    description: item.type === 'City' ? `${item.name}, ${item.region}` : item.name,
    structured_formatting: {
      main_text: item.name,
      secondary_text: item.type === 'Region' ? 'Region' : item.region
    },
    isStatic: true
  }))

  // Immediate update with static matches
  filteredResults.value = staticMatches
  
  // 2. API Search (Google Places)
  isLoading.value = true
  let apiMatches = []
  try {
    if (window.google?.maps?.places) {
      const service = new google.maps.places.AutocompleteService()
      const request = {
        input: query,
        sessionToken: sessionToken.value,
        types: ['geocode'],
      }
      
      // Add timeout to prevent hanging
      const apiPromise = new Promise((resolve) => {
        service.getPlacePredictions(request, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            resolve(predictions.map(p => ({
              ...p,
              name: p.structured_formatting.main_text,
              region: p.structured_formatting.secondary_text
            })))
          } else {
            resolve([])
          }
        })
      })

      // Race against timeout (5s) to accommodate slower mobile connections
      const timeoutPromise = new Promise(resolve => setTimeout(() => resolve([]), 5000))
      
      apiMatches = await Promise.race([apiPromise, timeoutPromise])
    }
  } catch (err) {
    console.warn('API Search failed', err)
  }

  // 3. Merge Strategies
  // Static matches first, then API matches
  // Remove duplicates? (Basic check on main_text)
  const combined = [...staticMatches, ...apiMatches]
  const unique = []
  const seen = new Set()
  
  combined.forEach(item => {
    const key = item.structured_formatting.main_text.toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      unique.push(item)
    }
  })

  filteredResults.value = unique
  isLoading.value = false
}

const select = (result) => {
  searchQuery.value = result.structured_formatting.main_text
  isOpen.value = false
  destinationSelected.value = true
  emit('select', {
    description: result.description,
    place_id: result.place_id,
    structured_formatting: result.structured_formatting
  })
}

const selectByText = (text) => {
  searchQuery.value = text
  isOpen.value = false
  destinationSelected.value = true
  emit('select', { description: text })
}

const selectNearby = async () => {
  if (!navigator.geolocation) {
    alert('Geolocation is not supported by your browser')
    return
  }
  
  navigator.geolocation.getCurrentPosition(async (position) => {
    const { latitude, longitude } = position.coords
    
    if (window.google?.maps?.Geocoder) {
      const geocoder = new google.maps.Geocoder()
      const latlng = { lat: latitude, lng: longitude }
      
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address
          searchQuery.value = address
          isOpen.value = false 
          emit('select', {
             description: address,
             coordinates: { lat: latitude, lng: longitude },
             place_id: results[0].place_id
          })
        }
      })
    }
  }, (err) => {
    console.warn(`Geolocation Error (${err.code}): ${err.message}`)
    if (err.code === 1) {
      alert('Please allow location access in your browser settings to use this feature.')
    } else {
      alert('Unable to retrieve your location. Please check your connection or try again later.')
    }
  }, {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0
  })
}

const detectUserCountry = async () => {
    // Check session storage first
    const CACHE_KEY = 'user_detected_country'
    try {
        const cached = sessionStorage.getItem(CACHE_KEY)
        if (cached) {
            currentCountry.value = cached
            return
        }
    } catch (e) {
        // ignore
    }

    try {
        const response = await fetch('https://ipapi.co/json/')
        if (!response.ok) throw new Error('IP API failed')
        const data = await response.json()
        if (data.country_name) {
            currentCountry.value = data.country_name
            // Cache the result
            try {
                sessionStorage.setItem(CACHE_KEY, data.country_name)
            } catch (e) {
                // ignore
            }
        }
    } catch (e) {
        // Silently fail and cache fallback
        try {
            sessionStorage.setItem(CACHE_KEY, 'Australia')
        } catch (e) {
            // ignore
        }
    }
}

const onResize = () => { windowWidth.value = typeof window !== 'undefined' ? window.innerWidth : 0 }

const handleClickOutside = (event) => {
  if (inputRef.value && !inputRef.value.contains(event.target) && !event.target.closest('.dropdown-content')) {
     isOpen.value = false
  }
}



onMounted(async () => {
  await loadGoogleMapsAPI()
  if (window.google?.maps?.places?.AutocompleteSessionToken) {
    sessionToken.value = new google.maps.places.AutocompleteSessionToken()
  }
  detectUserCountry()
  
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize)
    document.addEventListener('click', handleClickOutside)
    // Re-scroll when keyboard appears/disappears on mobile
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scrollInputIntoView)
    }
  }
})

onUnmounted(() => {
   if (typeof window !== 'undefined') {
    window.removeEventListener('resize', onResize)
    document.removeEventListener('click', handleClickOutside)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', scrollInputIntoView)
    }
  }
})
</script>

<style scoped>
/* Scoped styles mainly for specific overrides if Tailwind isn't enough */
.dropdown-content {
    max-height: 400px;
    overflow-y: auto;
}
</style>
